import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { useProfileInfo } from "../../contexts/ProfileInfoContext";
import Navbar from "../Navbar";
import Footer from "../Footer";
import EmployeeSidebar from "../EmployeeSidebar";

const EmployeeLayout = ({ children }) => {
    const { user } = useAuth();
    const { profileInfo, loading: profileLoading } = useProfileInfo();
    const navigate = useNavigate();
    const [sidebarOpen, setSidebarOpen] = useState(true);
    const [isMobile, setIsMobile] = useState(false);

    // Check if device is mobile
    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth < 768); // md breakpoint
        };
        
        checkMobile();
        window.addEventListener('resize', checkMobile);
        
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    // Set initial sidebar state based on device
    useEffect(() => {
        if (isMobile) {
            setSidebarOpen(false); // Hidden on mobile by default
        } else {
            setSidebarOpen(true); // Visible on desktop by default
        }
    }, [isMobile]);

    const toggleSidebar = () => {
        setSidebarOpen(!sidebarOpen);
    };

    // CRITICAL: Role-based access control - Employees ONLY
    useEffect(() => {
        if (!user) return;
        
        // Wait for profile to load
        if (profileLoading) return;
        
        // If profile exists but role is not Employee, redirect immediately
        if (profileInfo && profileInfo.role !== 'Employee') {
            console.warn(`[EmployeeLayout] Access denied: User role is ${profileInfo.role}, redirecting to appropriate dashboard`);
            // Redirect to correct dashboard based on actual role
            if (profileInfo.role === 'Employer') {
                navigate("/employer/dashboard", { replace: true });
            } else {
                // Unknown role or no role - redirect to role selection
                navigate("/role-selection", { replace: true });
            }
        }
    }, [user, profileInfo, profileLoading, navigate]);

    // Show loading while checking role
    if (user && profileLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-100">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-700"></div>
            </div>
        );
    }

    // If user exists but profile shows wrong role, don't render (redirect is happening)
    if (user && profileInfo && profileInfo.role !== 'Employee') {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-100">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-700"></div>
            </div>
        );
    }

    if (user) {
        return (
            <div className="flex h-screen bg-gray-100">
                {/* Desktop Sidebar - always visible on desktop */}
                {!isMobile && (
                    <div className="block">
                        <EmployeeSidebar isOpen={sidebarOpen} onToggle={toggleSidebar} isMobile={isMobile} />
                    </div>
                )}
                
                {/* Mobile Sidebar Overlay */}
                {isMobile && sidebarOpen && (
                    <>
                        {/* Backdrop */}
                        <div 
                            className="fixed inset-0 bg-black bg-opacity-50 z-40"
                            onClick={toggleSidebar}
                        />
                        {/* Sidebar */}
                        <div className="fixed left-0 top-0 h-full z-50">
                            <EmployeeSidebar isOpen={sidebarOpen} onToggle={toggleSidebar} isMobile={isMobile} />
                        </div>
                    </>
                )}
                
                <div className="flex-1 flex flex-col overflow-hidden">
                    <Navbar 
                        onToggleSidebar={toggleSidebar} 
                        sidebarOpen={sidebarOpen}
                        isMobile={isMobile}
                    />
                    <main className="flex-1 overflow-x-hidden overflow-y-auto p-6">
                        {children}
                    </main>
                </div>
            </div>
        );
    }

    return (
        <>
            <Navbar />
            {children}
            <Footer />
        </>
    );
};

export default EmployeeLayout;
